import {useRouter} from 'next/router';
import {useCallback, useEffect} from 'react';

import {RESPONSE_STATUSES} from '@/src/app/constants/api';
import useCourseDetailsActions from '@/src/app/store/courseDetails/useCourseDetailsActions';
import useUserActions from '@/src/app/store/user/useUserActions';
import {getAuthorizationToken} from '@/src/app/utils/authorizationToken';
import getCourseDetails from '@/src/courseDetails/utils/getCourseDetails';
import useLoading from '@/src/hooks/useLoading';
import useRoutePaths from '@/src/hooks/useRoutePaths';

import MainContainer from '../../main/container/MainContainer';
import {LoadingWrapper} from '../../ui';
import CourseDetailsLayout from '../components/CourseDetailsLayout';
import requestToggleCourseBookmark from '../utils/requestToggleCourseBookmark';

const CourseDetails = ({serverCourse}) => {
  const {
    course: clientCourse,
    contentCreator,
    toggleCourseBookmark,
    redirectToLoginPageWithReturn,
    reviews,
    setWritingReviewNotAllowed,
    setCourseDetails,
  } = useCourseDetailsActions();

  const course = clientCourse || serverCourse;

  const {isLoading, setLoading} = useLoading();
  const {isLoading: isBookmarkedLoading, setLoading: setBookmarkedLoading} =
    useLoading();
  const {user} = useUserActions();
  const {WATCH_COURSE_ROUTE, NOT_FOUND_ROUTE} = useRoutePaths();

  const {
    push,
    query: {slug},
  } = useRouter();
  const handleToggleCourseBookmarkClick = useCallback(async () => {
    if (!getAuthorizationToken()) {
      redirectToLoginPageWithReturn();
    }

    setBookmarkedLoading(true);

    const {status} = await requestToggleCourseBookmark(
      course.courseId,
      course.isBookmarked
    );

    if (status === RESPONSE_STATUSES.SUCCESS) {
      toggleCourseBookmark(!course.isBookmarked);
    }

    setBookmarkedLoading(false);
  }, [course.courseId, course.isBookmarked]);

  const handleWatchCourseClick = useCallback(() => {
    if (getAuthorizationToken() || course.isPublic) {
      return push(WATCH_COURSE_ROUTE.getLink(course.slug));
    }

    redirectToLoginPageWithReturn();
  }, [course]);

  useEffect(() => {
    requestGetCourse();
  }, [slug]);

  const requestGetCourse = useCallback(async () => {
    setLoading(true);
    // @ts-ignore
    const {status, responseBody} = await getCourseDetails(slug);

    if (status === RESPONSE_STATUSES.SUCCESS) {
      !responseBody.data.length
        ? push(NOT_FOUND_ROUTE.getLink())
        : setCourseDetails(responseBody);

      setLoading(false);
    } else {
      push(NOT_FOUND_ROUTE.getLink());
    }
  }, [slug]);

  return (
    <LoadingWrapper isLoading={isLoading}>
      <MainContainer
        widthContainer={MainContainer.CONTAINER_WIDTH.FULL_WIDTH}
        topOffset={MainContainer.TOP_OFFSET.OFFSET_0}
      >
        <CourseDetailsLayout
          course={course}
          ratingCount={course.ratingCount}
          isWritingReviewAllowed={
            course.isWritingReviewAllowed &&
            !course.isHasComment &&
            !(user?.user?.id && contentCreator?.id === user?.user?.id)
          }
          onWatchCourseClick={handleWatchCourseClick}
          isBookmarkedLoading={isBookmarkedLoading}
          isBookmarked={course.isBookmarked}
          onCourseBookMarkToggleClick={handleToggleCourseBookmarkClick}
          courseRequirements={course.courseRequirements}
          courseTargetGroups={course.courseTargetGroups}
          coursePreviewImage={course.coursePreviewImage}
          coursePreviewVideo={course.coursePreviewVideo}
          courseTitle={course.title}
          courseDescription={course.courseDescription}
          updateDate={course.updateDate}
          contentCreator={contentCreator}
          difficultyLevel={course.difficultyLevel}
          topicsCount={course.topicsCount}
          rating={course.rating}
          courseCategories={course.courseCategories}
          topics={course.topics}
          duration={course.duration}
          courseId={course.id}
          hasCertificate={course.hasCertificate}
          languages={course.languages}
          availableTranslations={course.availableTranslations}
          reviews={reviews}
          toggleLeaveReviewAllowed={setWritingReviewNotAllowed}
        />
      </MainContainer>
    </LoadingWrapper>
  );
};

export default CourseDetails;
